import { useEffect, useState } from "react";
import { allDrafts, saveDraft, type Draft } from "../drafts";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { SearchHit } from "../sync/api";
import { RichText } from "../text";
import { EmptyState, Page } from "./ui";
import { locale, t } from "../i18n";

// DraftsView is what one has written: drafts not yet sent, kept in this
// browser, and the messages one sent lately, newest first.
export function DraftsView() {
  const session = useSession();
  const state = useLantern();
  const me = state.me;
  const [show, setShow] = useState<"drafts" | "sent">("drafts");
  const [drafts, setDrafts] = useState<Draft[]>(() => (me ? allDrafts(me.id) : []));
  const [sent, setSent] = useState<SearchHit[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show !== "sent" || !me || sent) return;
    session.api.search(`from:@${me.handle}`).then(
      (r) => setSent(r.hits ?? []),
      (err) => setError(err instanceof Error ? err.message : String(err)),
    );
  }, [show, me, sent, session]);
  if (!me) return null;

  const where = (channel: string, thread: string | null) => session.channelName(channel) + (thread ? " · " + t("in a thread") : "");
  const open = (channel: string, thread: string | null) => navigate(thread ? `/c/${channel}/t/${thread}` : `/c/${channel}`);
  const named = (text: string) => <RichText text={session.mentionsIn(text)} name={(id) => session.name(id)} me={me.id} group={(id) => session.groupMention(id)} channel={(id) => session.channelName(id)} />;

  return (
    <Page className="drafts-view" narrow title={t("Drafts & sent")} description={t("Messages you started and have not sent, kept in this browser, and those you sent lately.")}>
      <div className="segmented" role="tablist">
        <button role="tab" aria-selected={show === "drafts"} className={show === "drafts" ? "active" : ""} onClick={() => setShow("drafts")}>
          {t("Drafts · {n}", { n: drafts.length })}
        </button>
        <button role="tab" aria-selected={show === "sent"} className={show === "sent" ? "active" : ""} onClick={() => setShow("sent")}>
          {t("Sent")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      {show === "drafts" &&
        (drafts.length === 0 ? (
          <EmptyState icon="✏️" title={t("No drafts")}>
            {t("What you type and leave unsent waits here, and in its channel.")}
          </EmptyState>
        ) : (
          <ul className="rows draft-list" aria-label={t("Drafts")}>
            {drafts.map((d) => (
              <li key={d.key}>
                <div className="row-main">
                  <button className="link" onClick={() => open(d.channel, d.thread)}>
                    {where(d.channel, d.thread)}
                  </button>
                  <div className="text">{named(d.text)}</div>
                </div>
                <button
                  className="small danger"
                  onClick={() => {
                    saveDraft(d.key, "");
                    setDrafts(drafts.filter((x) => x.key !== d.key));
                    session.store.update((s) => s.drafts.delete(d.channel));
                  }}
                >
                  {t("Delete")}
                </button>
              </li>
            ))}
          </ul>
        ))}
      {show === "sent" &&
        (sent === null ? (
          <p className="muted">{t("Loading…")}</p>
        ) : sent.length === 0 ? (
          <EmptyState icon="📤" title={t("Nothing sent yet")}>
            {t("Messages you send show here.")}
          </EmptyState>
        ) : (
          <ul className="rows draft-list" aria-label={t("Sent")}>
            {sent.map(({ message: m }) => (
              <li key={m.id}>
                <div className="row-main">
                  <span className="row-meta">
                    <button className="link" onClick={() => navigate(`/c/${m.channel_id}/m/${m.id}`)}>
                      {where(m.channel_id, m.thread_root_id)}
                    </button>{" "}
                    · {new Date(m.created_at).toLocaleString(locale, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </span>
                  <div className="text">
                    <RichText text={m.text} name={(id) => session.name(id)} me={me.id} group={(id) => session.groupMention(id)} channel={(id) => session.channelName(id)} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ))}
    </Page>
  );
}
